'use client';

import React from 'react';
import { styled } from 'styled-components';
import cs from 'classnames';
import * as Styled from '@lib/styles';
import type { CardColor } from '@types';
import ContentfulImage from './ContentfulImage';

export type QuoteCardProps = {
  /**
   * Color scheme of the card
   */
  colorScheme?: CardColor | 'transparent';
  /**
   * The author of the quote on the card
   */
  attribution: string;

  /**
   * The title of the attributing author
   */
  attributionTitle: string;
  /**
   * The card quote
   */
  quote: string;
  /**
   * Optional class name, nesc. for extending as a styled-component
   */
  className?: string;
  /**
   * URL of image
   */
  imageUrl: string;

  /**
   * Alt text for the logo being displayed
   */
  imageAltText: string;

  /**
   * Width of image being displayed
   */
  imageWidth: number;

  /**
   *  Height of image being displayed
   */
  imageHeight: number;
};

const QuoteCardContainer = styled.blockquote`
  border-radius: ${({ theme }) => theme.radius.std};
  display: grid;
  gap: ${({ theme }) => theme.spacing['1']};
  grid-template-rows: 1fr min-content min-content;
  height: 100%;
  line-height: ${({ theme }) => theme.typography.lineHeight.body};
  margin: 0;
  padding: ${({ theme }) => theme.spacing['2']};

  & > .QuoteCardImage {
    border-radius: unset;
    width: unset;
  }

  &.yellow {
    background-color: ${({ theme }) => theme.colors.yellow300};
  }

  &.orange {
    background-color: ${({ theme }) => theme.colors.orange400};
  }

  &.pink {
    background-color: ${({ theme }) => theme.colors.pink500};
  }

  &.purple {
    background-color: ${({ theme }) => theme.colors.purple500};
  }

  &.green {
    background-color: ${({ theme }) => theme.colors.green500};
  }

  &.lightBlue {
    background-color: ${({ theme }) => theme.colors.blue400};
  }

  &.blue {
    background-color: ${({ theme }) => theme.colors.blue200};
  }

  &.darkBlue {
    background-color: ${({ theme }) => theme.colors.blue700};
    color: ${({ theme }) => theme.colors.white};
  }

  &.white {
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.blue800};
  }

  &.transparent {
    background-color: transparent;
    color: inherit;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    padding: ${({ theme }) => theme.spacing['4']};
  }
`;

const QuoteWrapper = styled(Styled.BodyLg)`
  margin: 0;
  padding-top: ${({ theme }) => theme.spacing['6']};
`;

const AuthorText = styled.figcaption`
  display: flex;
  flex-direction: column;
  font-style: normal;
  font-weight: bold;

  ${({ theme }) => theme.mq.minWidth.large} {
    flex-direction: row;
  }
`;

const AuthorTitle = styled.span`
  font-weight: normal;
  margin-left: 0;

  ${({ theme }) => theme.mq.minWidth.large} {
    margin-left: ${({ theme }) => theme.spacing['1.5']};
  }
`;

const QuoteCard = ({
  className,
  colorScheme = 'blue',
  imageAltText,
  imageHeight,
  imageUrl,
  imageWidth,
  attribution,
  quote,
  attributionTitle,
}: QuoteCardProps) => {
  return (
    // The quoteCard classname is used to apply module specific styles; e.g. CustomerStory module.
    <QuoteCardContainer className={cs('quoteCard', colorScheme, className)}>
      <ContentfulImage
        alt={imageAltText}
        className="QuoteCardImage"
        height={imageHeight}
        src={imageUrl}
        width={imageWidth}
      />
      <QuoteWrapper>“{quote}”</QuoteWrapper>
      <AuthorText>
        {attribution} <AuthorTitle>{attributionTitle}</AuthorTitle>
      </AuthorText>
    </QuoteCardContainer>
  );
};

export default QuoteCard;
